import { adapterPlanogramItemToPlanogramItemCommon } from "@CommonAdapters/pog";
import { Pog } from "./pog";
import { PlanogramShelf } from "./shelf";
import { getShelfItemWidth, getShelfItemXOffsets } from "@CommonUtils/pog/shelfMerchandising";
import { uuid } from "@CommonUtils/uuid";
import { BaseProduct } from "@CommonTypes/merchflow/pog/product";

export type PlanogramItemOrientation = "FRONT" | "FRONT2" | "SIDE" | "SIDE2" | "TOP" | "TOP2";

/** The fixture type a product is intended to merchandise on, mirroring the backend `shelf_type` enum. */
export type ItemShelfType = "BASKET" | "HANGCELL" | "PEGBOARD" | "REGULAR" | "RISER";

export type PlanogramItemUpdateStatus = {
  operation?: string;
  changeType?: string;
};

export interface PlanogramItemCommonNonCircular extends BaseProduct {
  orientation: PlanogramItemOrientation;
  validOrientations: PlanogramItemOrientation[];
  merchandisingStyle: "UNIT" | "TRAY" | "CASE";
  /** Optional in the contract until the backend adapter populates it from the product's `shelf_type`. */
  shelfType?: ItemShelfType;
  trayWidth: number;
  trayHeight: number;
  trayDepth: number;
  caseWidth: number;
  caseHeight: number;
  caseDepth: number;
  displayWidth: number;
  displayHeight: number;
  displayDepth: number;
  dividerWidth: number;
  dividerHeight: number;
  noOfUnitsInCase: number;
  unitsCaseWide: number;
  unitsCaseDeep: number;
  unitsCaseHigh: number;
  noOfUnitsInTray: number;
  unitsTrayWide: number;
  unitsTrayDeep: number;
  unitsTrayHigh: number;
  overhangSqueezeDepthFlag: boolean;
  facingsRows: number;
  minFacingsRows: number;
  maxFacingsRows: number;
  /**
   * Depth-facing count stated by the source planogram (PSA `d_facings`), or
   * null when the source did not state one. When null, `getDepthFacings()`
   * falls back to the geometric `floor(shelf.depth / item depth)` guess —
   * exactly the pre-3.7 behaviour. Optional in data contracts (pogs older
   * than 3.7 lack it), normalized to null in the class.
   */
  unitsDeep?: number | null;
  zCappingFlag: boolean;
  /** Optional in data contracts, but initialized with default values in the class */
  xCapNum?: number;
  /** Optional in data contracts, but initialized with default values in the class */
  xCapOrientation?: PlanogramItemOrientation;
  /** Optional in data contracts, but initialized with default values in the class */
  yCapNum?: number;
  /** Optional in data contracts, but initialized with default values in the class */
  yCapOrientation?: PlanogramItemOrientation;
  /** Optional in data contracts, but initialized with default values in the class */
  zCapNum?: number;
  /** Optional in data contracts, but initialized with default values in the class */
  zCapOrientation?: PlanogramItemOrientation;
  updateStatus?: PlanogramItemUpdateStatus;
}

class PlanogramItemCommon implements PlanogramItemCommonNonCircular {
  uniqueId: string;
  id: string;
  productCode: string;
  name: string;
  upc: string;
  brand: string;
  variant: string;
  categoryCode: string;
  subCategory: string;
  size: string;
  unitWeight?: number | null;
  trayWeight?: number | null;
  caseWeight?: number | null;
  color: string;
  /** The unit `unitWeight` / tray / case are stated in. Null means unknown, never pounds. */
  weightUom: string | null;
  profit: number | null;
  quantity: number;
  price: number;
  salesAmount: number;
  orientation: PlanogramItemOrientation;
  validOrientations: PlanogramItemOrientation[];
  merchandisingStyle: "UNIT" | "TRAY" | "CASE";
  shelfType?: ItemShelfType;
  unitWidth: number;
  unitHeight: number;
  unitDepth: number;
  trayWidth: number;
  trayHeight: number;
  trayDepth: number;
  caseWidth: number;
  caseHeight: number;
  caseDepth: number;
  displayWidth: number;
  displayHeight: number;
  displayDepth: number;
  dividerWidth: number;
  dividerHeight: number;
  noOfUnitsInCase: number;
  unitsCaseWide: number;
  unitsCaseDeep: number;
  unitsCaseHigh: number;
  noOfUnitsInTray: number;
  unitsTrayWide: number;
  unitsTrayDeep: number;
  unitsTrayHigh: number;
  squeezeWidthPct: number;
  squeezeHeightPct: number;
  squeezeDepthPct: number;
  squeezeWidth: boolean;
  squeezeHeight: boolean;
  squeezeDepth: boolean;
  overhangSqueezeDepthFlag: boolean;
  cdt1: string;
  cdt1Priority: number;
  cdt2: string;
  cdt2Priority: number;
  cdt3: string;
  cdt3Priority: number;
  facings: number;
  minFacings: number;
  maxFacings: number;
  facingsRows: number;
  minFacingsRows: number;
  maxFacingsRows: number;
  /** See PlanogramItemCommonNonCircular. Always null unless the source planogram stated a depth-facing count. */
  unitsDeep: number | null;
  minOnShelf: number;
  minDaysOfSupply: number;
  maxDaysOfSupply: number;
  zCappingFlag: boolean;
  /** Non-optional in class instances (default: 0) to simplify UI state mapping */
  xCapNum: number;
  /** Non-optional in class instances (default: 'FRONT') to simplify UI state mapping */
  xCapOrientation: PlanogramItemOrientation;
  /** Non-optional in class instances (default: 0) to simplify UI state mapping */
  yCapNum: number;
  /** Non-optional in class instances (default: 'FRONT') to simplify UI state mapping */
  yCapOrientation: PlanogramItemOrientation;
  /** Non-optional in class instances (default: 0) to simplify UI state mapping */
  zCapNum: number;
  /** Non-optional in class instances (default: 'FRONT') to simplify UI state mapping */
  zCapOrientation: PlanogramItemOrientation;
  updateStatus?: PlanogramItemUpdateStatus;
  inCoreRange: boolean;
  newItemFlag: boolean;
  newItemStoreCodes?: string[] | undefined;
  dndFlag: boolean;
  spaceMix: number;
  rangedInBiggerOrSmallerStores?:
    | { approvedSmaller: number; allSmaller: number; approvedBigger: number; allBigger: number }
    | undefined;
  ranking: number;

  constructor(item: PlanogramItemCommonNonCircular) {
    this.uniqueId = item.uniqueId;
    this.id = item.id;
    this.productCode = item.productCode;
    this.name = item.name;
    this.upc = item.upc;
    this.brand = item.brand;
    this.variant = item.variant;
    this.categoryCode = item.categoryCode;
    this.subCategory = item.subCategory;
    this.size = item.size;
    this.unitWeight = item.unitWeight ?? null;
    this.trayWeight = item.trayWeight ?? null;
    this.caseWeight = item.caseWeight ?? null;
    this.color = item.color;
    // Normalised to null rather than left undefined: an absent key and a
    // stated null both mean "no measurement", and every pog older than this
    // field carries the absent form.
    this.weightUom = item.weightUom ?? null;
    this.profit = item.profit;
    this.quantity = item.quantity;
    this.price = item.price;
    this.salesAmount = item.salesAmount;
    this.orientation = item.orientation;
    this.validOrientations = [...item.validOrientations];
    this.merchandisingStyle = item.merchandisingStyle;
    this.shelfType = item.shelfType;
    this.unitWidth = item.unitWidth;
    this.unitHeight = item.unitHeight;
    this.unitDepth = item.unitDepth;
    this.trayWidth = item.trayWidth;
    this.trayHeight = item.trayHeight;
    this.trayDepth = item.trayDepth;
    this.caseWidth = item.caseWidth;
    this.caseHeight = item.caseHeight;
    this.caseDepth = item.caseDepth;
    this.displayWidth = item.displayWidth;
    this.displayHeight = item.displayHeight;
    this.displayDepth = item.displayDepth;
    this.dividerWidth = item.dividerWidth;
    this.dividerHeight = item.dividerHeight;
    this.noOfUnitsInCase = item.noOfUnitsInCase;
    this.unitsCaseWide = item.unitsCaseWide;
    this.unitsCaseDeep = item.unitsCaseDeep;
    this.unitsCaseHigh = item.unitsCaseHigh;
    this.noOfUnitsInTray = item.noOfUnitsInTray;
    this.unitsTrayWide = item.unitsTrayWide;
    this.unitsTrayDeep = item.unitsTrayDeep;
    this.unitsTrayHigh = item.unitsTrayHigh;
    this.squeezeWidthPct = item.squeezeWidthPct;
    this.squeezeHeightPct = item.squeezeHeightPct;
    this.squeezeDepthPct = item.squeezeDepthPct;
    this.squeezeWidth = item.squeezeWidth;
    this.squeezeHeight = item.squeezeHeight;
    this.squeezeDepth = item.squeezeDepth;
    this.overhangSqueezeDepthFlag = item.overhangSqueezeDepthFlag;
    this.cdt1 = item.cdt1;
    this.cdt1Priority = item.cdt1Priority;
    this.cdt2 = item.cdt2;
    this.cdt2Priority = item.cdt2Priority;
    this.cdt3 = item.cdt3;
    this.cdt3Priority = item.cdt3Priority;
    this.facings = item.facings;
    this.minFacings = item.minFacings;
    this.maxFacings = item.maxFacings;
    this.facingsRows = item.facingsRows;
    this.minFacingsRows = item.minFacingsRows;
    this.maxFacingsRows = item.maxFacingsRows;
    this.unitsDeep = item.unitsDeep ?? null;
    this.minOnShelf = item.minOnShelf;
    this.minDaysOfSupply = item.minDaysOfSupply;
    this.maxDaysOfSupply = item.maxDaysOfSupply;
    this.zCappingFlag = item.zCappingFlag;
    this.xCapNum = item.xCapNum ?? 0;
    this.xCapOrientation = item.xCapOrientation ?? "FRONT";
    this.yCapNum = item.yCapNum ?? 0;
    this.yCapOrientation = item.yCapOrientation ?? "FRONT";
    this.zCapNum = item.zCapNum ?? 0;
    this.zCapOrientation = item.zCapOrientation ?? "FRONT";
    this.updateStatus = item.updateStatus ? { ...item.updateStatus } : undefined;
    this.inCoreRange = item.inCoreRange;
    this.newItemFlag = item.newItemFlag;
    this.newItemStoreCodes = item.newItemStoreCodes ? [...item.newItemStoreCodes] : undefined;
    this.dndFlag = item.dndFlag;
    this.spaceMix = item.spaceMix;
    this.rangedInBiggerOrSmallerStores = item.rangedInBiggerOrSmallerStores
      ? { ...item.rangedInBiggerOrSmallerStores }
      : undefined;
    this.ranking = item.ranking;
  }

  getWidth = () => {
    return this.getOrientedDimensionForOrientation("width", this.orientation);
  };

  getHeight = () => {
    return this.getOrientedDimensionForOrientation("height", this.orientation);
  };

  getDepth = () => {
    return this.getOrientedDimensionForOrientation("depth", this.orientation);
  };

  getHorizontalFacings = () => {
    return Math.ceil(this.facings / this.facingsRows);
  };

  getVerticalFacings = () => {
    return Math.min(this.facings, this.facingsRows);
  };

  getUnits = () => {
    switch (this.merchandisingStyle) {
      case "UNIT":
        return 1;
      case "CASE":
        return this.noOfUnitsInCase;
      case "TRAY":
        return this.noOfUnitsInTray;
    }
  };

  getUnitsWide = () => {
    if (this.merchandisingStyle === "UNIT") {
      return 1;
    }

    const {
      unitsCaseWide,
      unitsCaseHigh,
      unitsCaseDeep,
      unitsTrayWide,
      unitsTrayHigh,
      unitsTrayDeep,
    } = this;

    if (this.merchandisingStyle === "CASE") {
      switch (this.orientation) {
        case "FRONT":
        case "TOP":
          return unitsCaseWide;
        case "FRONT2":
        case "SIDE2":
          return unitsCaseHigh;
        case "SIDE":
        case "TOP2":
          return unitsCaseDeep;
      }
    }

    switch (this.orientation) {
      case "FRONT":
      case "TOP":
        return unitsTrayWide;
      case "FRONT2":
      case "SIDE2":
        return unitsTrayHigh;
      case "SIDE":
      case "TOP2":
        return unitsTrayDeep;
    }
  };

  getUnitsHigh = () => {
    if (this.merchandisingStyle === "UNIT") {
      return 1;
    }

    const {
      unitsCaseWide,
      unitsCaseHigh,
      unitsCaseDeep,
      unitsTrayWide,
      unitsTrayHigh,
      unitsTrayDeep,
    } = this;

    if (this.merchandisingStyle === "CASE") {
      switch (this.orientation) {
        case "FRONT":
        case "SIDE":
          return unitsCaseHigh;
        case "FRONT2":
        case "TOP2":
          return unitsCaseWide;
        case "SIDE2":
        case "TOP":
          return unitsCaseDeep;
      }
    }

    switch (this.orientation) {
      case "FRONT":
      case "SIDE":
        return unitsTrayHigh;
      case "FRONT2":
      case "TOP2":
        return unitsTrayWide;
      case "SIDE2":
      case "TOP":
        return unitsTrayDeep;
    }
  };

  getCumulativeCdt1 = (): string[] => {
    return [this.variant, this.cdt1];
  };

  getCumulativeCdt2 = (): string[] => {
    return [this.variant, this.cdt1, this.cdt2];
  };

  getCumulativeCdt3 = (): string[] => {
    return [this.variant, this.cdt1, this.cdt2, this.cdt3];
  };

  private getOriginalDimensions = () => {
    return {
      width:
        (this.merchandisingStyle === "TRAY" && this.trayWidth) ||
        (this.merchandisingStyle === "CASE" && this.caseWidth) ||
        this.unitWidth,
      height:
        (this.merchandisingStyle === "TRAY" && this.trayHeight) ||
        (this.merchandisingStyle === "CASE" && this.caseHeight) ||
        this.unitHeight,
      depth:
        (this.merchandisingStyle === "TRAY" && this.trayDepth) ||
        (this.merchandisingStyle === "CASE" && this.caseDepth) ||
        this.unitDepth,
    };
  };

  private getSqueezedDimensions = () => {
    const { width, height, depth } = this.getOriginalDimensions();

    return {
      width: this.squeezeItemDimensionResult(width, this.squeezeWidth, this.squeezeWidthPct),
      height: this.squeezeItemDimensionResult(height, this.squeezeHeight, this.squeezeHeightPct),
      depth: this.squeezeItemDimensionResult(depth, this.squeezeDepth, this.squeezeDepthPct),
    };
  };

  private squeezeItemDimensionResult = (
    result: number,
    squeeze: boolean,
    squeezePercentage: number,
  ) => {
    return squeeze ? result * squeezePercentage : result;
  };

  getOrientedDimensionForOrientation = (
    dimension: "width" | "height" | "depth",
    orientation: PlanogramItemOrientation,
  ): number => {
    const { width, height, depth } = this.getSqueezedDimensions();

    let result: number;
    if (dimension === "width") {
      switch (orientation) {
        case "FRONT":
          result = width;
          break;
        case "FRONT2":
          result = height;
          break;
        case "SIDE":
          result = depth;
          break;
        case "SIDE2":
          result = height;
          break;
        case "TOP":
          result = width;
          break;
        case "TOP2":
          result = depth;
          break;
      }
      return result;
    } else if (dimension === "height") {
      switch (orientation) {
        case "FRONT":
          result = height;
          break;
        case "FRONT2":
          result = width;
          break;
        case "SIDE":
          result = height;
          break;
        case "SIDE2":
          result = depth;
          break;
        case "TOP":
          result = depth;
          break;
        case "TOP2":
          result = width;
          break;
      }
      return result;
    } else {
      switch (orientation) {
        case "FRONT":
          result = depth;
          break;
        case "FRONT2":
          result = depth;
          break;
        case "SIDE":
          result = width;
          break;
        case "SIDE2":
          result = width;
          break;
        case "TOP":
          result = height;
          break;
        case "TOP2":
          result = height;
          break;
      }
      return result;
    }
  };

  getXCappingWidth = (): number => {
    if (this.xCapNum <= 0) return 0;
    return this.xCapNum * this.getOrientedDimensionForOrientation("width", this.xCapOrientation);
  };

  getYCappingHeight = (): number => {
    if (this.yCapNum <= 0) return 0;
    return this.yCapNum * this.getOrientedDimensionForOrientation("height", this.yCapOrientation);
  };

  // TODO: Z-capping properties (zCapNum and zCapOrientation) are successfully parsed and stored,
  // but are currently not utilized in 2D layout rendering or canvas boundary checks.
  // This is kept here for future 3D planogram rendering support.
  getZCappingDepth = (): number => {
    if (this.zCapNum <= 0) return 0;
    return this.zCapNum * this.getOrientedDimensionForOrientation("depth", this.zCapOrientation);
  };
}

export interface PlanogramItemNonCircular extends PlanogramItemCommonNonCircular {
  shelf: string | null;
}

export class PlanogramItem extends PlanogramItemCommon {
  shelf: PlanogramShelf;

  constructor(item: PlanogramItemCommonNonCircular, shelf: PlanogramShelf) {
    super(item);
    this.shelf = shelf;
  }

  /**
   * Units deep for this placement. The source planogram's stated count
   * (`unitsDeep`) wins when present; otherwise the geometric guess of how
   * many fit behind each other on this shelf. This is the ONLY place that
   * guess is written — getCapacity() and getUnitsOnShelf() must go through
   * here, so a stated unitsDeep reaches every derived number.
   */
  getDepthFacings = () => {
    return this.unitsDeep ?? Math.floor(this.shelf.depth / this.getDepth());
  };

  getCoordinates = () => {
    const mergedShelves = this.shelf.getMergedShelves();
    const mergedXLeft = mergedShelves[0].getXLeft();
    const mergedMerchandisableWidth = this.shelf.getMergedMerchandisableWidth();

    // Build ordered list of items across merged shelves (left-to-right)
    const mergedItems: PlanogramItem[] = [];
    for (const shelf of mergedShelves) {
      for (const item of shelf.items) {
        mergedItems.push(item);
      }
    }

    const itemWidths = mergedItems.map(getShelfItemWidth);
    const itemXOffsets = getShelfItemXOffsets({
      itemWidths,
      merchandisableWidth: mergedMerchandisableWidth,
      overflowPolicy: "compress-to-boundary",
    });

    for (let i = 0; i < mergedItems.length; i++) {
      const shelfItem = mergedItems[i];
      const itemWidth = itemWidths[i];
      const itemHeight =
        shelfItem.getHeight() * shelfItem.getVerticalFacings() + shelfItem.getYCappingHeight();

      if (shelfItem === this) {
        const xLeft = mergedXLeft + itemXOffsets[i];
        return {
          xLeft,
          xRight: xLeft + itemWidth,
          yBottom: this.shelf.getYBottom() + this.shelf.thickness,
          yTop: this.shelf.getYBottom() + this.shelf.thickness + itemHeight,
        };
      }
    }

    // This shouldn't happen.
    throw new Error(`Item ${this.uniqueId} not found on merged shelves`);
  };

  getCapacity = () => {
    // Loose z-capped units in the depth left over behind the stock. Note the
    // remainder is measured against getDepthFacings(): when a source-stated
    // unitsDeep is LOWER than the geometric maximum, the leftover depth grows
    // and this term counts more loose units — deliberate, since depth not
    // occupied by planned stock is exactly where loose product goes.
    const looseProducts =
      this.getUnitsWide() *
      this.getUnitsHigh() *
      Math.floor((this.shelf.depth - this.getDepth() * this.getDepthFacings()) / this.unitDepth);

    return (
      this.facings *
      (this.getDepthFacings() * this.getUnits() + (this.zCappingFlag ? looseProducts : 0))
    );
  };

  getDaysOfSupply = () => {
    return (this.getCapacity() / this.quantity) * 7;
  };

  getDaysOfSupplyPercentage = () => {
    return (this.getDaysOfSupply() / this.minDaysOfSupply) * 100;
  };

  getMOS = () => {
    return this.getCapacity() / this.minOnShelf;
  };

  getMOSPercentage = () => {
    return this.getMOS() * 100;
  };

  getUnitsOnShelf = () => {
    return this.facings * this.getDepthFacings() * this.getUnits();
  };

  getCasesOnShelf = () => {
    if (this.merchandisingStyle === "UNIT") return null;
    const uos = this.getUnitsOnShelf();

    if (!uos) return null;
    return Math.round(uos / this.getUnits());
  };

  duplicate = (shelf: PlanogramShelf, uniqueId = uuid()) => {
    return new PlanogramItem(
      {
        ...adapterPlanogramItemToPlanogramItemCommon(this),
        uniqueId,
        dndFlag: true,
      },
      shelf,
    );
  };

  unrange = () => {
    const unrangedItem = new UnrangedPlanogramItem(
      {
        ...this,
        potentialShelves: [this.shelf.uniqueId],
        reasons: [],
      },
      this.shelf.bay.planogram.pog,
    );

    // New object into unranged items.
    this.shelf.bay.planogram.pog.unrangedItems.unshift(unrangedItem);

    // Remove all matching products from planogram.
    for (const bay of this.shelf.bay.planogram.bays) {
      for (const shelf of bay.shelves) {
        shelf.items = shelf.items.filter((item) => item.productCode !== this.productCode);
      }
    }

    return unrangedItem;
  };

  rangeItem = (rangedItem: UnrangedPlanogramItem | PlanogramItem, side: string) => {
    this.rangeItems([rangedItem], side);
  };

  rangeItems = (rangedItems: (UnrangedPlanogramItem | PlanogramItem)[], side: string) => {
    // Ranged items need to be looped in reverse order
    for (let i = rangedItems.length - 1; i >= 0; i--) {
      const rangedItem = rangedItems[i];
      let finalRangedItem: PlanogramItem;

      // Planogram item
      if ("shelf" in rangedItem) {
        rangedItem.shelf.items = rangedItem.shelf.items.filter(
          (item) => item.uniqueId !== rangedItem.uniqueId,
        );
        finalRangedItem = rangedItem;
        finalRangedItem.shelf = this.shelf;

        // Unranged item
      } else {
        rangedItem.pog.unrangedItems = rangedItem.pog.unrangedItems.filter(
          (unrangedItem) => unrangedItem !== rangedItem,
        );
        finalRangedItem = new PlanogramItem(rangedItem, this.shelf);
      }

      finalRangedItem.dndFlag = true;
      const thisItemIndex = this.shelf.items.findIndex((item) => item === this);
      this.shelf.items.splice(thisItemIndex + (side === "right" ? 1 : 0), 0, finalRangedItem);
    }
  };

  delete = (storeCodes: string[]) => {
    this.shelf.bay.planogram.pog.deletedItems.push({
      uniqueId: this.uniqueId,
      id: this.id,
      productCode: this.productCode,
      name: this.name,
      upc: this.upc,
      brand: this.brand,
      variant: this.variant,
      categoryCode: this.categoryCode,
      subCategory: this.subCategory,
      size: this.size,
      color: this.color,
      profit: this.profit,
      quantity: this.quantity,
      price: this.price,
      salesAmount: this.salesAmount,
      orientation: this.orientation,
      validOrientations: [...this.validOrientations],
      merchandisingStyle: this.merchandisingStyle,
      unitWidth: this.unitWidth,
      unitHeight: this.unitHeight,
      unitDepth: this.unitDepth,
      trayWidth: this.trayWidth,
      trayHeight: this.trayHeight,
      trayDepth: this.trayDepth,
      caseWidth: this.caseWidth,
      caseHeight: this.caseHeight,
      caseDepth: this.caseDepth,
      displayWidth: this.displayWidth,
      displayHeight: this.displayHeight,
      displayDepth: this.displayDepth,
      dividerWidth: this.dividerWidth,
      dividerHeight: this.dividerHeight,
      noOfUnitsInCase: this.noOfUnitsInCase,
      unitsCaseWide: this.unitsCaseWide,
      unitsCaseDeep: this.unitsCaseDeep,
      unitsCaseHigh: this.unitsCaseHigh,
      noOfUnitsInTray: this.noOfUnitsInTray,
      unitsTrayWide: this.unitsTrayWide,
      unitsTrayDeep: this.unitsTrayDeep,
      unitsTrayHigh: this.unitsTrayHigh,
      squeezeWidthPct: this.squeezeWidthPct,
      squeezeHeightPct: this.squeezeHeightPct,
      squeezeDepthPct: this.squeezeDepthPct,
      squeezeWidth: this.squeezeWidth,
      squeezeHeight: this.squeezeHeight,
      squeezeDepth: this.squeezeDepth,
      overhangSqueezeDepthFlag: this.overhangSqueezeDepthFlag,
      cdt1: this.cdt1,
      cdt1Priority: this.cdt1Priority,
      cdt2: this.cdt2,
      cdt2Priority: this.cdt2Priority,
      cdt3: this.cdt3,
      cdt3Priority: this.cdt3Priority,
      facings: this.facings,
      minFacings: this.minFacings,
      maxFacings: this.maxFacings,
      facingsRows: this.facingsRows,
      minFacingsRows: this.minFacingsRows,
      maxFacingsRows: this.maxFacingsRows,
      unitsDeep: this.unitsDeep,
      minOnShelf: this.minOnShelf,
      minDaysOfSupply: this.minDaysOfSupply,
      maxDaysOfSupply: this.maxDaysOfSupply,
      zCappingFlag: this.zCappingFlag,
      inCoreRange: this.inCoreRange,
      newItemFlag: this.newItemFlag,
      dndFlag: this.dndFlag,
      spaceMix: this.spaceMix,
      ranking: this.ranking,
      storeCodes,
    });
    this.shelf.items = this.shelf.items.filter((item) => item.uniqueId !== this.uniqueId);
  };
}

export interface UnrangedPlanogramItemNonCircular extends PlanogramItemNonCircular {
  potentialShelves: string[];
  reasons: string[];
}

export class UnrangedPlanogramItem extends PlanogramItemCommon {
  potentialShelves: string[];
  reasons: string[];
  pog: Pog;

  constructor(item: Omit<UnrangedPlanogramItemNonCircular, "shelf">, pog: Pog) {
    super(item);
    this.potentialShelves = [...item.potentialShelves];
    this.reasons = [...item.reasons];
    this.pog = pog;
  }

  range = () => {
    // Remove from unranged items.
    this.pog.unrangedItems = this.pog.unrangedItems.filter(
      (item) => item.productCode !== this.productCode,
    );

    // Add to shelf.
    for (const potentialShelf of this.potentialShelves) {
      const shelf = this.pog.planogram.getShelf(potentialShelf);
      if (shelf) {
        shelf.addItems([this]);
        break;
      }
    }
  };

  delete = (storeCodes: string[]) => {
    this.pog.deletedItems.push({
      uniqueId: this.uniqueId,
      id: this.id,
      productCode: this.productCode,
      name: this.name,
      upc: this.upc,
      brand: this.brand,
      variant: this.variant,
      categoryCode: this.categoryCode,
      subCategory: this.subCategory,
      size: this.size,
      color: this.color,
      profit: this.profit,
      quantity: this.quantity,
      price: this.price,
      salesAmount: this.salesAmount,
      orientation: this.orientation,
      validOrientations: [...this.validOrientations],
      merchandisingStyle: this.merchandisingStyle,
      unitWidth: this.unitWidth,
      unitHeight: this.unitHeight,
      unitDepth: this.unitDepth,
      trayWidth: this.trayWidth,
      trayHeight: this.trayHeight,
      trayDepth: this.trayDepth,
      caseWidth: this.caseWidth,
      caseHeight: this.caseHeight,
      caseDepth: this.caseDepth,
      displayWidth: this.displayWidth,
      displayHeight: this.displayHeight,
      displayDepth: this.displayDepth,
      dividerWidth: this.dividerWidth,
      dividerHeight: this.dividerHeight,
      noOfUnitsInCase: this.noOfUnitsInCase,
      unitsCaseWide: this.unitsCaseWide,
      unitsCaseDeep: this.unitsCaseDeep,
      unitsCaseHigh: this.unitsCaseHigh,
      noOfUnitsInTray: this.noOfUnitsInTray,
      unitsTrayWide: this.unitsTrayWide,
      unitsTrayDeep: this.unitsTrayDeep,
      unitsTrayHigh: this.unitsTrayHigh,
      squeezeWidthPct: this.squeezeWidthPct,
      squeezeHeightPct: this.squeezeHeightPct,
      squeezeDepthPct: this.squeezeDepthPct,
      squeezeWidth: this.squeezeWidth,
      squeezeHeight: this.squeezeHeight,
      squeezeDepth: this.squeezeDepth,
      overhangSqueezeDepthFlag: this.overhangSqueezeDepthFlag,
      cdt1: this.cdt1,
      cdt1Priority: this.cdt1Priority,
      cdt2: this.cdt2,
      cdt2Priority: this.cdt2Priority,
      cdt3: this.cdt3,
      cdt3Priority: this.cdt3Priority,
      facings: this.facings,
      minFacings: this.minFacings,
      maxFacings: this.maxFacings,
      facingsRows: this.facingsRows,
      minFacingsRows: this.minFacingsRows,
      maxFacingsRows: this.maxFacingsRows,
      unitsDeep: this.unitsDeep,
      minOnShelf: this.minOnShelf,
      minDaysOfSupply: this.minDaysOfSupply,
      maxDaysOfSupply: this.maxDaysOfSupply,
      zCappingFlag: this.zCappingFlag,
      inCoreRange: this.inCoreRange,
      newItemFlag: this.newItemFlag,
      dndFlag: this.dndFlag,
      spaceMix: this.spaceMix,
      ranking: this.ranking,
      storeCodes,
      rangedInBiggerOrSmallerStores: this.rangedInBiggerOrSmallerStores
        ? { ...this.rangedInBiggerOrSmallerStores }
        : undefined,
    });
    this.pog.unrangedItems = this.pog.unrangedItems.filter(
      (unrangedItem) => unrangedItem.uniqueId !== this.uniqueId,
    );
  };
}
